import * as THREE from 'three';
import { C, type BuildingDef, type CivDef } from '../../../gameplay/types';
import { ModelBuilder, Part, Surf, type PartOpts } from './builder';
import { civStyle, isModern, isPrimitive, isThatched, matFor, type Flavour, type Mat } from './buildingStyles';

/**
 * Surface texture by colour role for the building being made (walls, roof, stonework, timber), so
 * the textures follow the materials without every shape having to name its own.
 */
let SURF = new Map<number, Surf>();
const T = (color: number, team = 0, part: Part = Part.Static, pivot?: [number, number, number]): PartOpts => ({ color, team, part, pivot, surf: team ? Surf.None : SURF.get(color) });

export interface BuildingVisual {
  geometry: THREE.BufferGeometry;
  /** Local chimney top positions (smoke emitters). */
  smoke: THREE.Vector3[];
  height: number;
  /** Fraction of the footprint the building itself covers (the rest is its yard). */
  scale: number;
}

/**
 * Buildings grow with the ages: a quarter of their plot's area in the Settlement Age, filling it in
 * the Industrial Age. These are the matching edge lengths (square roots of 25%, 44%, 62.5%, 81%, 100%).
 */
export const AGE_SCALE = [0.5, 0.66, 0.79, 0.9, 1];

const darken = (c: number, f: number) => new THREE.Color(c).multiplyScalar(f).getHex();
const mix = (a: number, b: number, t: number) => new THREE.Color(a).lerp(new THREE.Color(b), t).getHex();

/** Building models change with every age (see buildingStyles.ts). */
export function ageTier(age: number): number {
  return Math.max(0, Math.min(4, age));
}

function crenellations(b: ModelBuilder, w: number, d: number, y: number, color: number): void {
  const n = Math.max(2, Math.round(w / 0.28));
  const m = Math.max(2, Math.round(d / 0.28));
  for (let i = 0; i < n; i++) {
    const x = -w / 2 + (i + 0.5) * (w / n);
    b.box(w / n / 1.8, 0.12, 0.08, T(color), x, y + 0.06, d / 2 - 0.04);
    b.box(w / n / 1.8, 0.12, 0.08, T(color), x, y + 0.06, -d / 2 + 0.04);
  }
  for (let i = 0; i < m; i++) {
    const z = -d / 2 + (i + 0.5) * (d / m);
    b.box(0.08, 0.12, d / m / 1.8, T(color), w / 2 - 0.04, y + 0.06, z);
    b.box(0.08, 0.12, d / m / 1.8, T(color), -w / 2 + 0.04, y + 0.06, z);
  }
}

function roof(b: ModelBuilder, style: string, w: number, d: number, y: number, color: number, trim: number, cx = 0, cz = 0): number {
  switch (style) {
    case 'gable': {
      // The ridge runs along the long side.
      const h = Math.min(1.0, Math.min(w, d) * 0.42);
      if (w >= d) {
        b.prism(d + 0.12, h, w + 0.16, T(color), cx, y, cz, Math.PI / 2);
        b.prism(d + 0.14, 0.04, w + 0.18, T(trim), cx, y - 0.02, cz, Math.PI / 2);
      } else {
        b.prism(w + 0.16, h, d + 0.12, T(color), cx, y, cz);
        b.prism(w + 0.18, 0.04, d + 0.14, T(trim), cx, y - 0.02, cz);
      }
      return y + h;
    }
    case 'hip': {
      const h = Math.min(0.95, Math.min(w, d) * 0.4);
      b.pyramid(w + 0.18, h, d + 0.18, T(color), cx, y, cz);
      return y + h;
    }
    case 'pagoda': {
      const h = Math.min(0.6, Math.min(w, d) * 0.26);
      b.pyramid(w + 0.5, h, d + 0.5, T(color), cx, y, cz);
      b.box(w * 0.6, 0.16, d * 0.6, T(trim), cx, y + h * 0.8, cz);
      b.pyramid(w * 0.8, h * 0.9, d * 0.8, T(color), cx, y + h * 0.8 + 0.08, cz);
      b.cyl(0.02, 0.02, 0.25, 5, T(0xc8a030), cx, y + h * 1.7 + 0.2, cz);
      return y + h * 1.7 + 0.08;
    }
    case 'dome': {
      const r = Math.min(1.1, Math.min(w, d) * 0.36);
      b.cyl(r * 1.02, r * 1.02, 0.12, 14, T(trim), cx, y + 0.06, cz);
      b.add(new THREE.SphereGeometry(r, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), T(color), cx, y + 0.12, cz);
      b.cyl(0.02, 0.03, 0.3, 5, T(0xd0b040), cx, y + 0.12 + r + 0.12, cz);
      return y + r + 0.12;
    }
    case 'pyramid': {
      // Stepped (Mesoamerican) or smooth monument roof.
      let yy = y;
      let ww = w;
      let dd = d;
      for (let i = 0; i < 3; i++) {
        b.box(ww, 0.18, dd, T(i % 2 ? darken(color, 0.9) : color), cx, yy + 0.09, cz);
        yy += 0.18;
        ww *= 0.75;
        dd *= 0.75;
      }
      b.box(ww, 0.22, dd, T(trim), cx, yy + 0.11, cz);
      return yy + 0.22;
    }
    case 'cone': {
      const r = Math.max(w, d) * 0.62;
      b.cone(r, r * 1.3, 10, T(color), cx, y + r * 0.65, cz);
      return y + r * 1.3;
    }
    case 'low': {
      // Shallow industrial roof.
      const h = Math.min(0.35, Math.min(w, d) * 0.18);
      if (w >= d) b.prism(d + 0.08, h, w + 0.1, T(color), cx, y, cz, Math.PI / 2);
      else b.prism(w + 0.1, h, d + 0.08, T(color), cx, y, cz);
      return y + h;
    }
    case 'flat':
    default:
      b.box(w + 0.06, 0.06, d + 0.06, T(trim), cx, y + 0.03, cz);
      return y + 0.06;
  }
}

function banner(b: ModelBuilder, x: number, y: number, z: number, h = 0.7): void {
  // Taller pole for the large flag.
  const ph = h + 0.25;
  b.cyl(0.02, 0.02, ph, 5, T(0x5a4a3a), x, y + ph / 2, z);
  b.sphere(0.035, T(0xc8a040), x, y + ph + 0.02, z);
  b.box(0.01, 0.4, 0.6, T(0xffffff, 1, Part.Flag, [x, y + ph, z]), x, y + ph - 0.22, z + 0.31);
}

function farmField(b: ModelBuilder, w: number, d: number): void {
  // Soil reaches well below ground level and sits clearly above it, so uneven ground under the
  // field never pokes through or fights with it.
  b.box(w - 0.1, 0.36, d - 0.1, T(0x6a4a2a), 0, -0.13, 0);
  const rows = 7;
  for (let i = 0; i < rows; i++) {
    const z = -d / 2 + 0.25 + (i * (d - 0.5)) / (rows - 1);
    b.box(w - 0.35, 0.09, 0.12, T(i % 2 ? 0x8a9a3a : 0x9aa840, 0, Part.Sway, [0, 0, z]), 0, 0.07, z);
  }
  for (let i = 0; i <= 6; i++) {
    const t = -w / 2 + (i * w) / 6;
    b.box(0.04, 0.16, 0.04, T(0x5a4028), t, 0.08, d / 2 - 0.03);
    b.box(0.04, 0.16, 0.04, T(0x5a4028), t, 0.08, -d / 2 + 0.03);
    b.box(0.04, 0.16, 0.04, T(0x5a4028), w / 2 - 0.03, 0.08, t);
    b.box(0.04, 0.16, 0.04, T(0x5a4028), -w / 2 + 0.03, 0.08, t);
  }
  b.box(w, 0.025, 0.025, T(0x6a5038), 0, 0.12, d / 2 - 0.03);
  b.box(w, 0.025, 0.025, T(0x6a5038), 0, 0.12, -d / 2 + 0.03);
  b.box(0.025, 0.025, d, T(0x6a5038), w / 2 - 0.03, 0.12, 0);
  b.box(0.025, 0.025, d, T(0x6a5038), -w / 2 + 0.03, 0.12, 0);
}

// ---------------------------------------------------------------------------
// Building composition. Every building type has its own layout so it reads as
// what it is (forge, stables, windmill, church...). The owner's civilisation
// supplies palette and roof tradition; each age brings its own building material
// (see buildingStyles.ts).
// ---------------------------------------------------------------------------

interface Ctx {
  b: ModelBuilder;
  arch: CivDef['architecture'];
  age: number;
  mat: Mat;
  flavour: Flavour;
  /** Set by a canvas block: the shape its roof must take. */
  tent?: { yurt: boolean; r: number; cx: number; cz: number };
  wall: number;
  frame: number;
  roofColor: number;
  trim: number;
  stone: number;
  smoke: THREE.Vector3[];
  top: number;
}

const WOOD = 0x7a5a36;
const DARK_WOOD = 0x4a3420;
const GLASS = 0x34465a;
const IRON = 0x3a3a40;
const FIRE = 0xffa040;
const GOLD = 0xd4a838;
const THATCH = 0xc4a45c;
const SHINGLE = 0x5a4632;

/** A band around all four faces of a block (weave, stone courses, mortar lines, trim). */
function ring(c: Ctx, cx: number, cz: number, w: number, d: number, y: number, h: number, color: number, out = 0.008, t = 0.02): void {
  const b = c.b;
  // Front and back run the full width; the sides fit between them, so no faces overlap at corners.
  b.box(w + 2 * out, h, t, T(color), cx, y, cz + d / 2 + out - t / 2);
  b.box(w + 2 * out, h, t, T(color), cx, y, cz - d / 2 - out + t / 2);
  b.box(t, h, d + 2 * out - 2 * t, T(color), cx + w / 2 + out - t / 2, y, cz);
  b.box(t, h, d + 2 * out - 2 * t, T(color), cx - w / 2 - out + t / 2, y, cz);
}

/** Vertical members along all four faces (stakes, bamboo, battens, ribs). */
function uprights(c: Ctx, cx: number, cz: number, w: number, d: number, y0: number, h: number, spacing: number, size: number, color: number | ((i: number) => number), round = false, skipDoor = false): void {
  const b = c.b;
  const col = (i: number) => (typeof color === 'number' ? color : color(i));
  const put = (x: number, z: number, i: number) => {
    if (round) b.cyl(size, size, h, 5, T(col(i)), x, y0 + h / 2, z);
    else b.box(size, h, size, T(col(i)), x, y0 + h / 2, z);
  };
  const nx = Math.max(2, Math.round(w / spacing));
  const nz = Math.max(2, Math.round(d / spacing));
  const o = round ? size * 0.7 : size / 2;
  for (let i = 0; i <= nx; i++) {
    const x = cx - w / 2 + (i * w) / nx;
    if (!(skipDoor && Math.abs(x - cx) < 0.26)) put(x, cz + d / 2 + o, i);
    put(x, cz - d / 2 - o, i);
  }
  for (let i = 1; i < nz; i++) {
    const z = cz - d / 2 + (i * d) / nz;
    put(cx + w / 2 + o, z, i);
    put(cx - w / 2 - o, z, i);
  }
}

/** Columns across the front face with a lintel (Grecian porticos, marble colonnades). */
function colonnade(c: Ctx, cx: number, cz: number, w: number, d: number, baseY: number, h: number, color: number, door: boolean): void {
  const b = c.b;
  const n = Math.max(2, Math.round(w / 0.42));
  const z = cz + d / 2 + 0.12;
  for (let i = 0; i <= n; i++) {
    const x = cx - w / 2 + 0.08 + (i * (w - 0.16)) / n;
    if (door && Math.abs(x - cx) < 0.24) continue;
    b.cyl(0.05, 0.06, h - 0.1, 8, T(color), x, baseY + (h - 0.1) / 2, z);
    b.box(0.13, 0.04, 0.13, T(color), x, baseY + 0.02, z);
  }
  b.box(w + 0.04, 0.08, 0.3, T(color), cx, baseY + h - 0.06, cz + d / 2 + 0.1);
}

type Face = 'front' | 'back' | 'left' | 'right';

/** How far windows stand off the wall face, clear of the logs, poles and frames laid over it. */
function standoff(c: Ctx): number {
  if (c.mat === 'stone' && (c.flavour === 'japanese' || c.flavour === 'chinese')) return 0.065;
  switch (c.mat) {
    case 'wicker':
      return 0.05;
    case 'log':
      return 0.075;
    case 'bamboo':
      return 0.06;
    case 'daub':
      return 0.03;
    case 'plank':
      return 0.035;
    case 'woodGrass':
      return 0.075;
    case 'bambooGrass':
    case 'carved':
      return 0.065;
    default:
      return 0.012;
  }
}

/** A window with frame, shutters and sill on one face of a wall box. */
function windowOn(c: Ctx, face: Face, cx: number, cz: number, w: number, d: number, along: number, y: number, size = 1): void {
  const b = c.b;
  if (isPrimitive(c.mat)) {
    // A simple opening with a rough frame.
    const ow = 0.16 * size;
    const oh = 0.18 * size;
    const po = standoff(c);
    if (face === 'front' || face === 'back') {
      const z = cz + (face === 'front' ? d / 2 + po : -d / 2 - po);
      b.box(ow + 0.05, oh + 0.05, 0.02, T(DARK_WOOD), cx + along, y, z);
      b.box(ow, oh, 0.03, T(0x1e1a18), cx + along, y, z);
    } else {
      const x = cx + (face === 'right' ? w / 2 + po : -w / 2 - po);
      b.box(0.02, oh + 0.05, ow + 0.05, T(DARK_WOOD), x, y, cz + along);
      b.box(0.03, oh, ow, T(0x1e1a18), x, y, cz + along);
    }
    return;
  }
  const ww = 0.24 * size;
  const wh = 0.3 * size;
  const shutter = darken(c.frame, 1.05);
  if (face === 'front' || face === 'back') {
    const z = cz + (face === 'front' ? d / 2 + standoff(c) : -d / 2 - standoff(c));
    const x = cx + along;
    b.box(ww + 0.06, wh + 0.06, 0.02, T(c.trim), x, y, z);
    b.box(ww, wh, 0.03, T(GLASS), x, y, z);
    b.box(0.025, wh, 0.035, T(c.trim), x, y, z);
    b.box(ww, 0.025, 0.035, T(c.trim), x, y, z);
    b.box(ww + 0.12, 0.04, 0.07, T(darken(c.wall, 0.8)), x, y - wh / 2 - 0.03, z);
    if (c.age <= 1) {
      b.box(0.1, wh, 0.02, T(shutter), x - ww / 2 - 0.07, y, z);
      b.box(0.1, wh, 0.02, T(shutter), x + ww / 2 + 0.07, y, z);
    }
  } else {
    const x = cx + (face === 'right' ? w / 2 + standoff(c) : -w / 2 - standoff(c));
    const z = cz + along;
    b.box(0.02, wh + 0.06, ww + 0.06, T(c.trim), x, y, z);
    b.box(0.03, wh, ww, T(GLASS), x, y, z);
    b.box(0.035, wh, 0.025, T(c.trim), x, y, z);
    b.box(0.035, 0.025, ww, T(c.trim), x, y, z);
    b.box(0.07, 0.04, ww + 0.12, T(darken(c.wall, 0.8)), x, y - wh / 2 - 0.03, z);
  }
}

/** Front door with frame and team-coloured awning. */
function doorOn(c: Ctx, cx: number, cz: number, d: number, baseY: number, dw = 0.36, dh = 0.55): void {
  const z = cz + d / 2 + (c.mat === 'log' ? 0.06 : c.mat === 'bamboo' || c.mat === 'bambooGrass' ? 0.04 : 0);
  c.b.box(dw + 0.08, dh + 0.06, 0.03, T(c.trim), cx, baseY + dh / 2, z + 0.01);
  c.b.box(dw, dh, 0.04, T(DARK_WOOD), cx, baseY + dh / 2, z + 0.015);
  c.b.box(0.03, 0.03, 0.05, T(0xc8a040), cx + dw * 0.3, baseY + dh * 0.5, z + 0.03);
  c.b.box(dw + 0.22, 0.05, 0.18, T(0xffffff, 1), cx, baseY + dh + 0.07, z + 0.08);
}

/**
 * Walls of a building block with windows on every face and optional door, dressed in the age's
 * material. Returns the wall-top height.
 */
function block(c: Ctx, cx: number, cz: number, w: number, d: number, h: number, opts: { baseY?: number; door?: boolean; windows?: number; color?: number } = {}): number {
  const b = c.b;
  const baseY = opts.baseY ?? 0.1;
  const skin = opts.color === undefined;
  const color = opts.color ?? c.wall;
  if (skin && c.mat === 'canvas') return tentWalls(c, cx, cz, w, d, h, baseY, !!opts.door);
  c.tent = undefined;
  if (skin && c.mat === 'stone' && (c.flavour === 'japanese' || c.flavour === 'chinese')) {
    // Battered stone base under plastered (Japanese) or red-lacquered (Chinese) upper walls.
    const sh = h * (c.flavour === 'japanese' ? 0.42 : 0.3);
    const g = new THREE.CylinderGeometry(Math.SQRT1_2 * 0.94, Math.SQRT1_2, 1, 4, 1);
    g.rotateY(Math.PI / 4);
    b.add(g, T(c.stone), cx, baseY + sh / 2, cz, 0, 0, 0, w + 0.16, sh, d + 0.16);
    b.box(w, h - sh, d, T(color), cx, baseY + sh + (h - sh) / 2, cz);
    uprights(c, cx, cz, w, d, baseY + sh, h - sh, 0.55, 0.06, c.flavour === 'japanese' ? DARK_WOOD : 0x8a2418);
    ring(c, cx, cz, w, d, baseY + h - 0.04, 0.06, c.flavour === 'japanese' ? DARK_WOOD : GOLD, 0.03, 0.03);
  } else if (c.arch.roofStyle === 'flat' && (c.mat === 'woodGrass' || c.mat === 'stone' || c.mat === 'marble' || !skin)) {
    // Battered (inward-sloping) walls, Egyptian style.
    const g = new THREE.CylinderGeometry(Math.SQRT1_2 * 0.9, Math.SQRT1_2, 1, 4, 1);
    g.rotateY(Math.PI / 4);
    b.add(g, T(color), cx, baseY + h / 2, cz, 0, 0, 0, w, h, d);
  } else b.box(w, h, d, T(color), cx, baseY + h / 2, cz);
  if (skin) wallSkin(c, cx, cz, w, d, h, baseY, !!opts.door);
  else {
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.box(0.1, h, 0.1, T(darken(color, 0.85)), cx + (sx * w) / 2, baseY + h / 2, cz + (sz * d) / 2);
    b.box(w + 0.04, 0.05, d + 0.04, T(darken(color, 0.8)), cx, baseY + h - 0.05, cz);
  }
  const wy = baseY + h * 0.58;
  const per = (opts.windows ?? 1) * (c.mat === 'wicker' || c.mat === 'bamboo' ? 0.5 : 1);
  if (per > 0 && h > 0.45 && !isModern(c.mat)) {
    const nx = Math.max(1, Math.round((w / 0.65) * per));
    const nz = Math.max(1, Math.round((d / 0.65) * per));
    for (let i = 0; i < nx; i++) {
      const along = -w / 2 + ((i + 0.5) * w) / nx;
      if (!(opts.door && Math.abs(along) < 0.32)) windowOn(c, 'front', cx, cz, w, d, along, wy);
      windowOn(c, 'back', cx, cz, w, d, along, wy);
    }
    for (let i = 0; i < nz; i++) {
      const along = -d / 2 + ((i + 0.5) * d) / nz;
      windowOn(c, 'left', cx, cz, w, d, along, wy);
      windowOn(c, 'right', cx, cz, w, d, along, wy);
    }
  } else if (per > 0 && h > 0.45) {
    // Industrial window bands, one per storey.
    const floors = Math.max(1, Math.round(h / 0.5));
    for (let f = 0; f < floors; f++) ring(c, cx, cz, w - 0.2, d - 0.2, baseY + (h * (f + 0.6)) / floors, 0.16, GLASS, 0.11, 0.02);
  }
  if (opts.door) doorOn(c, cx, cz, d, baseY, Math.min(0.42, w * 0.28), Math.min(0.6, h * 0.75));
  if (skin && h >= 0.6 && w >= 1.2 && (c.mat === 'marble' || (c.mat === 'stone' && (c.flavour === 'iberian' || c.flavour === 'egyptian')))) {
    colonnade(c, cx, cz, w, d, baseY, h, c.mat === 'marble' ? 0xf4f0e8 : darken(c.stone, 1.1), !!opts.door);
  }
  c.top = Math.max(c.top, baseY + h);
  return baseY + h;
}

/** Surface detail for each building material. */
function wallSkin(c: Ctx, cx: number, cz: number, w: number, d: number, h: number, baseY: number, door: boolean): void {
  const b = c.b;
  const top = baseY + h;
  switch (c.mat) {
    case 'wicker': {
      // Woven panels between rough stakes.
      for (let y = baseY + 0.05, i = 0; y < top - 0.03; y += 0.055, i++) ring(c, cx, cz, w, d, y, 0.035, i % 2 ? darken(c.wall, 0.72) : darken(c.wall, 1.12), i % 2 ? 0.006 : 0.014);
      uprights(c, cx, cz, w, d, baseY - 0.02, h - 0.02, 0.45, 0.028, DARK_WOOD, true, door);
      break;
    }
    case 'log': {
      // Stacked logs with ends crossing at the corners.
      const r = 0.065;
      for (let y = baseY + r, i = 0; y < top - 0.02; y += r * 2, i++) {
        const col = i % 2 ? 0x8a6238 : 0x7a5430;
        const off = i % 2 ? 0 : 0.012;
        for (const sz of [-1, 1]) b.cyl(r, r, w + 0.16, 6, T(col), cx, y + off, cz + sz * (d / 2), 0, 0, Math.PI / 2);
        for (const sx of [-1, 1]) b.cyl(r, r, d + 0.16, 6, T(darken(col, 0.95)), cx + sx * (w / 2), y + off + r * 0.5, cz, Math.PI / 2, 0, 0);
      }
      break;
    }
    case 'bamboo': {
      uprights(c, cx, cz, w, d, baseY, h + 0.04, Math.max(0.13, w / 14), 0.03, (i) => (i % 2 ? 0xb8a860 : 0xa89850), true, door);
      for (const f of [0.3, 0.85]) ring(c, cx, cz, w, d, baseY + h * f, 0.035, 0x7a6a3a, 0.07, 0.03);
      break;
    }
    case 'daub': {
      // Close timber framing with braces.
      const n = Math.max(2, Math.round(w / 0.5));
      const bay = w / n;
      for (let i = 0; i <= n; i++) {
        const x = cx - w / 2 + i * bay;
        for (const sz of [-1, 1]) b.box(0.05, h, 0.02, T(c.frame), x, baseY + h / 2, cz + sz * (d / 2 + 0.005));
        if (i < n && i % 2 === 0) {
          const len = Math.hypot(bay, h * 0.55);
          const a = Math.atan2(bay, h * 0.55);
          for (const sz of [-1, 1]) b.box(0.04, len, 0.02, T(c.frame), x + bay / 2, baseY + h * 0.4, cz + sz * (d / 2 + 0.006), 0, 0, sz * a);
        }
      }
      const m = Math.max(2, Math.round(d / 0.5));
      for (let i = 1; i < m; i++) for (const sx of [-1, 1]) b.box(0.02, h, 0.05, T(c.frame), cx + sx * (w / 2 + 0.005), baseY + h / 2, cz - d / 2 + (i * d) / m);
      for (const f of [0.12, 0.98]) ring(c, cx, cz, w, d, baseY + h * f - 0.02, 0.05, c.frame, 0.008);
      break;
    }
    case 'plank': {
      uprights(c, cx, cz, w, d, baseY, h, 0.18, 0.025, darken(c.wall, 0.75), false, door);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.box(0.13, h + 0.04, 0.13, T(DARK_WOOD), cx + (sx * w) / 2, baseY + h / 2, cz + (sz * d) / 2);
      ring(c, cx, cz, w, d, top - 0.05, 0.1, DARK_WOOD, 0.02, 0.04);
      ring(c, cx, cz, w, d, baseY + 0.05, 0.1, DARK_WOOD, 0.02, 0.04);
      break;
    }
    case 'woodGrass':
    case 'bambooGrass': {
      const bamboo = c.mat === 'bambooGrass';
      uprights(c, cx, cz, w, d, baseY, h, bamboo ? 0.45 : 0.7, bamboo ? 0.035 : 0.07, bamboo ? 0xb0a058 : WOOD, bamboo, door);
      ring(c, cx, cz, w, d, top - 0.04, 0.08, bamboo ? 0x8a7a40 : WOOD, 0.03, 0.04);
      ring(c, cx, cz, w, d, baseY + 0.06, 0.12, darken(c.wall, 0.75), 0.012);
      break;
    }
    case 'stone': {
      const course = darken(c.wall, 0.86);
      for (let y = baseY + 0.22; y < top - 0.06; y += 0.22) ring(c, cx, cz, w, d, y, 0.02, course, 0.006);
      // Corner quoins.
      for (let y = baseY + 0.06, i = 0; y < top - 0.04; y += 0.12, i++) {
        const s = i % 2 ? 0.16 : 0.12;
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.box(s, 0.1, s, T(darken(c.wall, 1.08)), cx + sx * (w / 2 - s / 2 + 0.02), y, cz + sz * (d / 2 - s / 2 + 0.02));
      }
      break;
    }
    case 'carved': {
      // Lacquered or carved frames over plaster, finished with gilt bands.
      uprights(c, cx, cz, w, d, baseY, h, 0.38, 0.06, c.frame, false, door);
      ring(c, cx, cz, w, d, baseY + 0.06, 0.12, c.frame, 0.03, 0.03);
      ring(c, cx, cz, w, d, top - 0.05, 0.1, c.frame, 0.035, 0.03);
      ring(c, cx, cz, w, d, top - 0.12, 0.025, GOLD, 0.04, 0.02);
      if (c.flavour === 'german') ring(c, cx, cz, w, d, baseY + h * 0.5, 0.04, c.frame, 0.03, 0.03);
      break;
    }
    case 'marble': {
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.box(0.12, h, 0.12, T(0xf6f2ea), cx + (sx * w) / 2, baseY + h / 2, cz + (sz * d) / 2);
      ring(c, cx, cz, w, d, baseY + 0.05, 0.1, darken(c.wall, 0.9), 0.02, 0.03);
      ring(c, cx, cz, w, d, top - 0.04, 0.08, GOLD, 0.025, 0.03);
      break;
    }
    case 'steel': {
      uprights(c, cx, cz, w, d, baseY, h, 0.24, 0.035, darken(c.wall, 0.75));
      ring(c, cx, cz, w, d, top - 0.04, 0.08, darken(c.wall, 0.6), 0.02, 0.03);
      break;
    }
    case 'concrete': {
      const floors = Math.max(1, Math.round(h / 0.5));
      for (let f = 1; f <= floors; f++) ring(c, cx, cz, w, d, baseY + (h * f) / floors - 0.03, 0.05, darken(c.wall, 0.82), 0.012);
      break;
    }
    case 'brick': {
      for (let y = baseY + 0.12; y < top - 0.06; y += 0.12) ring(c, cx, cz, w, d, y, 0.014, darken(c.wall, 1.22), 0.005);
      for (let y = baseY + 0.07, i = 0; y < top - 0.04; y += 0.14, i++) {
        const s = i % 2 ? 0.15 : 0.11;
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.box(s, 0.12, s, T(0xd8d0c0), cx + sx * (w / 2 - s / 2 + 0.02), y, cz + sz * (d / 2 - s / 2 + 0.02));
      }
      ring(c, cx, cz, w, d, top - 0.04, 0.08, 0xd8d0c0, 0.025, 0.03);
      break;
    }
  }
}

/** Canvas: a round yurt for squarish blocks, a ridge tent for long ones. */
function tentWalls(c: Ctx, cx: number, cz: number, w: number, d: number, h: number, baseY: number, door: boolean): number {
  const b = c.b;
  const yurt = Math.abs(w - d) < 0.3 * Math.max(w, d);
  if (yurt) {
    const r = Math.min(w, d) / 2;
    const hw = Math.min(h * 0.7, 0.6);
    b.cyl(r, r * 1.02, hw, 16, T(c.wall), cx, baseY + hw / 2, cz);
    b.cyl(r + 0.012, r + 0.012, 0.05, 16, T(c.trim), cx, baseY + hw * 0.8, cz);
    b.cyl(r + 0.012, r + 0.012, 0.04, 16, T(c.trim), cx, baseY + hw * 0.25, cz);
    if (door) {
      b.box(0.3, Math.min(0.42, hw * 0.85), 0.04, T(0xa8382a), cx, baseY + Math.min(0.42, hw * 0.85) / 2, cz + r + 0.01);
      b.box(0.36, 0.04, 0.05, T(DARK_WOOD), cx, baseY + Math.min(0.42, hw * 0.85), cz + r + 0.015);
    }
    c.tent = { yurt, r, cx, cz };
    c.top = Math.max(c.top, baseY + hw);
    return baseY + hw;
  }
  const hw = Math.min(h * 0.32, 0.3);
  b.box(w, hw, d, T(c.wall), cx, baseY + hw / 2, cz);
  ring(c, cx, cz, w, d, baseY + hw - 0.03, 0.04, c.trim, 0.008);
  if (door) b.box(0.3, hw + 0.2, 0.04, T(0x2a201a), cx, baseY + (hw + 0.2) / 2, cz + d / 2 + 0.01);
  c.tent = { yurt, r: 0, cx, cz };
  c.top = Math.max(c.top, baseY + hw);
  return baseY + hw;
}

function tentRoof(c: Ctx, cx: number, cz: number, w: number, d: number, y: number): number {
  const b = c.b;
  const t = c.tent;
  c.tent = undefined;
  if (t?.yurt) {
    const r = t.r * 1.06;
    const h = Math.max(0.3, r * 0.55);
    b.cone(r, h, 16, T(darken(c.wall, 0.94)), cx, y + h / 2, cz);
    b.cyl(r * 0.2, r * 0.22, 0.06, 10, T(DARK_WOOD), cx, y + h * 0.82, cz);
    c.smoke.push(new THREE.Vector3(cx, y + h, cz));
    c.top = Math.max(c.top, y + h);
    return y + h;
  }
  const along = w >= d;
  const span = along ? d : w;
  const len = along ? w : d;
  const h = Math.min(1.1, span * 0.6);
  b.prism(span + 0.12, h, len + 0.04, T(darken(c.wall, 0.96)), cx, y, cz, along ? Math.PI / 2 : 0);
  // Ridge poles at both ends.
  for (const s of [-1, 1]) {
    const px = along ? cx + s * (len / 2 + 0.03) : cx;
    const pz = along ? cz : cz + s * (len / 2 + 0.03);
    b.cyl(0.025, 0.025, y + h + 0.12, 5, T(DARK_WOOD), px, (y + h + 0.12) / 2, pz);
  }
  b.box(0.01, 0.24, 0.4, T(0xffffff, 1, Part.Flag, [cx, y + h + 0.12, cz]), cx, y + h, cz + 0.2);
  c.top = Math.max(c.top, y + h + 0.12);
  return y + h;
}

/** Civilisation roof on a block. */
function civRoof(c: Ctx, cx: number, cz: number, w: number, d: number, y: number, override?: string): number {
  if (c.mat === 'canvas' && c.tent) return tentRoof(c, cx, cz, w, d, y);
  let style = override ?? (c.arch.roofStyle === 'pyramid' ? 'flat' : c.arch.roofStyle);
  if (style === 'gable' && c.arch.roofStyle === 'hip') style = 'hip';
  const m = c.mat;
  const hard = style === 'flat' || style === 'dome' || style === 'pyramid';
  if (isThatched(m) || m === 'canvas') {
    // Straw, reed and canvas: steep and thick; reed roofs are flat only where it never rains.
    if (!(style === 'flat' && c.flavour === 'egyptian' && m !== 'canvas') && (hard || (style === 'pagoda' && isPrimitive(m)))) style = 'hip';
  } else if (m === 'log' || m === 'plank') {
    if (hard || style === 'pagoda') style = c.flavour === 'steppe' ? 'hip' : 'gable';
  } else if (m === 'steel') style = style === 'flat' ? 'flat' : 'low';
  else if (m === 'concrete') style = 'flat';
  const color = c.roofColor;
  if (isThatched(m) && style !== 'flat') roof(c.b, style, w + 0.12, d + 0.12, y - 0.05, darken(color, 0.78), darken(color, 0.7), cx, cz);
  // A flat roof is drawn in its trim colour: plain materials keep their own roof colour.
  const trim = style === 'flat' && (isModern(m) || isThatched(m)) ? color : c.trim;
  const top = roof(c.b, style, w, d, y, color, trim, cx, cz);
  if (style === 'flat') {
    if (m === 'stone' || m === 'brick' || m === 'marble' || m === 'daub') crenellationsAt(c, cx, cz, w, d, y, darken(c.wall, 0.92));
    else ring(c, cx, cz, w, d, y + 0.06, 0.1, darken(c.wall, 0.85), 0.02, 0.04);
  }
  if (style === 'pagoda') {
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) c.b.cyl(0.05, 0.05, y - 0.1, 6, T(c.mat === 'bambooGrass' ? 0xb0a058 : 0xa82a1e), cx + (sx * w) / 2, (y + 0.1) / 2, cz + (sz * d) / 2);
  }
  c.top = Math.max(c.top, top);
  return top;
}

function crenellationsAt(c: Ctx, cx: number, cz: number, w: number, d: number, y: number, color: number): void {
  const g = new ModelBuilder();
  crenellations(g, w, d, y, color);
  c.b.merge(g, cx, 0, cz);
}

function chimney(c: Ctx, x: number, z: number, h: number, w = 0.16): void {
  const color = c.age >= 4 || c.mat === 'brick' ? 0x8a4a36 : isPrimitive(c.mat) ? 0x7a6a58 : darken(c.stone, 0.9);
  c.b.box(w, h, w, T(color), x, h / 2, z);
  c.b.box(w + 0.05, 0.05, w + 0.05, T(darken(color, 0.8)), x, h, z);
  c.smoke.push(new THREE.Vector3(x, h + 0.05, z));
  c.top = Math.max(c.top, h);
}

/** Round tower with civ-appropriate cap. */
function tower(c: Ctx, x: number, z: number, r: number, h: number, color = c.wall): void {
  const b = c.b;
  b.cyl(r, r * 1.1, h, 12, T(color), x, h / 2 + 0.05, z);
  for (let i = 0; i < 3; i++) {
    const a = (i * Math.PI * 2) / 3 + 0.4;
    b.box(0.05, 0.18, 0.03, T(0x1e1a18), x + Math.sin(a) * (r + 0.005), h * (0.45 + i * 0.12), z + Math.cos(a) * (r + 0.005), 0, a, 0);
  }
  const s =
    isThatched(c.mat) || isPrimitive(c.mat) || c.mat === 'plank'
      ? 'gable'
      : isModern(c.mat) || (c.mat === 'stone' && (c.flavour === 'british' || c.flavour === 'german'))
        ? 'flat'
        : c.arch.roofStyle;
  if (s === 'dome') b.add(new THREE.SphereGeometry(r * 1.08, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), T(c.roofColor), x, h + 0.05, z);
  else if (s === 'pagoda') b.pyramid(r * 3, r * 1.2, r * 3, T(c.roofColor), x, h + 0.05, z);
  else if (s === 'flat' || s === 'pyramid') {
    const g = new ModelBuilder();
    crenellations(g, r * 1.9, r * 1.9, h + 0.05, color);
    b.merge(g, x, 0, z);
  } else b.cone(r * 1.35, r * 2.3, 12, T(c.roofColor), x, h + 0.05 + r * 1.15, z);
  c.top = Math.max(c.top, h + r * 2.3);
}

// ---- Props ----
function crate(b: ModelBuilder, x: number, z: number, s = 0.2, y = 0): void {
  b.box(s, s, s, T(0x9a7444), x, y + s / 2, z);
  b.box(s + 0.01, 0.025, s + 0.01, T(DARK_WOOD), x, y + s * 0.8, z);
}
function barrel(b: ModelBuilder, x: number, z: number): void {
  b.cyl(0.09, 0.08, 0.22, 8, T(0x7a5a32), x, 0.11, z);
  b.cyl(0.095, 0.095, 0.02, 8, T(IRON), x, 0.16, z);
}
function logPile(b: ModelBuilder, x: number, z: number, n = 3, len = 0.9): void {
  for (let i = 0; i < n; i++) for (let j = 0; j <= i % 2; j++) b.cyl(0.075, 0.075, len, 7, T(i % 2 ? 0x8a6238 : 0x7a5430), x + (j - 0.3) * 0.15, 0.08 + i * 0.12, z, 0, 0, Math.PI / 2);
}
function hay(b: ModelBuilder, x: number, z: number): void {
  b.box(0.3, 0.18, 0.2, T(0xd4b456), x, 0.09, z);
}
function horse(b: ModelBuilder, x: number, z: number, ry: number, color = 0x6a4a2a): void {
  const g = new ModelBuilder();
  g.box(0.14, 0.15, 0.4, T(color), 0, 0.32, 0);
  g.box(0.08, 0.2, 0.09, T(color), 0, 0.45, 0.2, 0.5, 0, 0);
  g.box(0.07, 0.07, 0.15, T(color), 0, 0.54, 0.27);
  for (const [lx, lz] of [[-0.05, 0.14], [0.05, 0.14], [-0.05, -0.14], [0.05, -0.14]]) g.box(0.04, 0.26, 0.04, T(color), lx, 0.13, lz);
  b.merge(g, x, 0, z, ry);
}
function dummy(b: ModelBuilder, x: number, z: number): void {
  b.cyl(0.025, 0.025, 0.6, 6, T(WOOD), x, 0.3, z);
  b.box(0.3, 0.04, 0.04, T(WOOD), x, 0.45, z);
  b.cyl(0.09, 0.08, 0.22, 8, T(0xc8b07a), x, 0.42, z);
  b.sphere(0.07, T(0xc8b07a), x, 0.6, z);
}
function target(b: ModelBuilder, x: number, z: number): void {
  b.box(0.03, 0.45, 0.03, T(WOOD), x - 0.1, 0.22, z - 0.05, 0.25, 0, 0);
  b.box(0.03, 0.45, 0.03, T(WOOD), x + 0.1, 0.22, z - 0.05, 0.25, 0, 0);
  b.cyl(0.18, 0.18, 0.04, 14, T(0xe8e0cc), x, 0.4, z, Math.PI / 2 - 0.25, 0, 0);
  b.cyl(0.12, 0.12, 0.045, 14, T(0xc02a2a), x, 0.4, z + 0.003, Math.PI / 2 - 0.25, 0, 0);
  b.cyl(0.05, 0.05, 0.05, 10, T(0xe8e0cc), x, 0.4, z + 0.006, Math.PI / 2 - 0.25, 0, 0);
}
function weaponRack(b: ModelBuilder, x: number, z: number): void {
  b.box(0.5, 0.04, 0.04, T(WOOD), x, 0.35, z);
  b.box(0.04, 0.4, 0.04, T(WOOD), x - 0.25, 0.2, z);
  b.box(0.04, 0.4, 0.04, T(WOOD), x + 0.25, 0.2, z);
  for (let i = 0; i < 5; i++) {
    b.cyl(0.01, 0.01, 0.75, 4, T(DARK_WOOD), x - 0.2 + i * 0.1, 0.38, z - 0.03, 0.15, 0, 0);
    b.cone(0.022, 0.07, 4, T(0xb8bcc4), x - 0.2 + i * 0.1, 0.78, z + 0.03);
  }
}
function fence(b: ModelBuilder, cx: number, cz: number, w: number, d: number, openFront = true): void {
  const posts = (len: number) => Math.max(2, Math.round(len / 0.35));
  const side = (x0: number, z0: number, x1: number, z1: number) => {
    const n = posts(Math.hypot(x1 - x0, z1 - z0));
    for (let i = 0; i <= n; i++) b.box(0.04, 0.22, 0.04, T(0x5a4028), x0 + ((x1 - x0) * i) / n, 0.11, z0 + ((z1 - z0) * i) / n);
    const len = Math.hypot(x1 - x0, z1 - z0);
    b.box(Math.abs(x1 - x0) > 0.01 ? len : 0.03, 0.03, Math.abs(z1 - z0) > 0.01 ? len : 0.03, T(0x6a5038), (x0 + x1) / 2, 0.17, (z0 + z1) / 2);
  };
  side(cx - w / 2, cz - d / 2, cx + w / 2, cz - d / 2);
  side(cx - w / 2, cz - d / 2, cx - w / 2, cz + d / 2);
  side(cx + w / 2, cz - d / 2, cx + w / 2, cz + d / 2);
  if (openFront) {
    side(cx - w / 2, cz + d / 2, cx - w / 6, cz + d / 2);
    side(cx + w / 6, cz + d / 2, cx + w / 2, cz + d / 2);
  } else side(cx - w / 2, cz + d / 2, cx + w / 2, cz + d / 2);
}
function openShed(c: Ctx, cx: number, cz: number, w: number, d: number, h: number): number {
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) c.b.box(0.07, h, 0.07, T(WOOD), cx + (sx * (w / 2 - 0.05)), 0.1 + h / 2, cz + (sz * (d / 2 - 0.05)));
  c.b.box(w, 0.06, 0.06, T(WOOD), cx, h + 0.08, cz + d / 2 - 0.05);
  c.b.box(w, 0.06, 0.06, T(WOOD), cx, h + 0.08, cz - d / 2 + 0.05);
  c.b.box(w, h * 0.6, 0.05, T(c.age <= 1 ? WOOD : c.wall), cx, 0.1 + h * 0.3, cz - d / 2 + 0.03);
  return civRoof(c, cx, cz, w, d, h + 0.1, c.arch.roofStyle === 'dome' ? 'hip' : undefined);
}

/** Wall connection bits: neighbours to the north (-z), east (+x), south (+z) and west (-x). */
export const WALL_N = 1;
export const WALL_E = 2;
export const WALL_S = 4;
export const WALL_W = 8;
const WALL_DIRS: [number, number, number][] = [[WALL_N, 0, -1], [WALL_E, 1, 0], [WALL_S, 0, 1], [WALL_W, -1, 0]];

/**
 * One wall piece that joins up with its neighbours. Bits 0-3 of `variant` say which sides connect;
 * bits 4-7 say which of those neighbours are masonry. Each piece draws the half of every
 * connection on its own tile, so neighbouring halves meet at the tile edge.
 */
function wallPiece(key: string, stone: number, variant: number): BuildingVisual {
  const b = new ModelBuilder();
  const wood = 0x8a6238;
  const woodDark = 0x5e4228;
  SURF = new Map([[wood, Surf.Wood], [woodDark, Surf.Wood], [stone, Surf.Stone], [darken(stone, 0.85), Surf.Stone], [darken(stone, 0.9), Surf.Stone]]);
  let mask = variant & 15;
  let stoneMask = (variant >> 4) & 15;
  const segment = key === 'fence' || key === 'stoneWall';
  // A lone segment still reads as a short run of wall.
  if (segment && mask === 0) {
    mask = WALL_E | WALL_W;
    stoneMask = key === 'stoneWall' ? mask : 0;
  }
  const rails = (dx: number, dz: number, from: number) => {
    const len = 0.5 - from;
    const mid = from + len / 2;
    for (const y of [0.3, 0.56]) b.box(dx ? len : 0.05, 0.06, dz ? len : 0.05, T(wood), dx * mid, y, dz * mid);
  };
  const stoneArm = (dx: number, dz: number, from: number, h: number) => {
    const len = 0.5 - from;
    const mid = from + len / 2;
    const th = 0.62;
    b.box(dx ? len : th, h, dz ? len : th, T(stone), dx * mid, h / 2, dz * mid);
    b.box(dx ? len : th + 0.04, 0.05, dz ? len : th + 0.04, T(darken(stone, 0.85)), dx * mid, 0.05, dz * mid);
    // Merlons along both faces.
    for (let i = 0; i < 2; i++) {
      const t = from + (i + 0.5) * (len / 2);
      for (const side of [-1, 1]) {
        const ox = dx ? dx * t : side * (th / 2 - 0.05);
        const oz = dz ? dz * t : side * (th / 2 - 0.05);
        b.box(dx ? 0.12 : 0.1, 0.16, dz ? 0.12 : 0.1, T(stone), ox, h + 0.08, oz);
      }
    }
  };
  let top = 0.7;
  switch (key) {
    case 'fence':
      b.box(0.1, 0.72, 0.1, T(woodDark), 0, 0.36, 0);
      b.box(0.13, 0.05, 0.13, T(woodDark), 0, 0.74, 0);
      // Timber always meets its neighbour with rails; masonry pieces draw their own stone halves.
      for (const [bit, dx, dz] of WALL_DIRS) if (mask & bit) rails(dx, dz, 0.05);
      top = 0.8;
      break;
    case 'fencePost':
      b.box(0.24, 0.95, 0.24, T(woodDark), 0, 0.475, 0);
      b.box(0.3, 0.06, 0.3, T(wood), 0, 0.98, 0);
      b.box(0.14, 0.12, 0.14, T(woodDark), 0, 1.07, 0);
      // Iron bands.
      for (const y of [0.25, 0.7]) b.box(0.26, 0.04, 0.26, T(IRON), 0, y, 0);
      for (const [bit, dx, dz] of WALL_DIRS) if (mask & bit) rails(dx, dz, 0.12);
      top = 1.15;
      break;
    case 'stonePillar':
      b.box(0.56, 0.14, 0.56, T(darken(stone, 0.85)), 0, 0.07, 0);
      b.box(0.44, 1.4, 0.44, T(stone), 0, 0.84, 0);
      b.box(0.54, 0.1, 0.54, T(darken(stone, 0.9)), 0, 1.58, 0);
      b.box(0.3, 0.16, 0.3, T(stone), 0, 1.71, 0);
      b.box(0.02, 0.22, 0.12, T(0xffffff, 1), 0.23, 1.05, 0);
      b.box(0.02, 0.22, 0.12, T(0xffffff, 1), -0.23, 1.05, 0);
      for (const [bit, dx, dz] of WALL_DIRS) if (mask & bit) (stoneMask & bit ? stoneArm(dx, dz, 0.2, 1.2) : rails(dx, dz, 0.2));
      top = 1.8;
      break;
    default: {
      // Stone wall segment: a core block plus a masonry arm toward each neighbour.
      b.box(0.62, 1.2, 0.62, T(stone), 0, 0.6, 0);
      b.box(0.66, 0.05, 0.66, T(darken(stone, 0.85)), 0, 0.05, 0);
      b.box(0.12, 0.16, 0.12, T(stone), 0.25, 1.28, 0.25);
      b.box(0.12, 0.16, 0.12, T(stone), -0.25, 1.28, -0.25);
      for (const [bit, dx, dz] of WALL_DIRS) if (mask & bit) stoneArm(dx, dz, 0.3, 1.2);
      top = 1.4;
    }
  }
  return { geometry: b.build(), smoke: [], height: top, scale: 1 };
}

export function buildBuildingGeometry(def: BuildingDef, civ: CivDef, age: number, variant = 0, full = false): BuildingVisual {
  if (def.classes & C.Wall) return wallPiece(def.key, mix(0x9a948a, civ.architecture.wall, 0.35), variant);
  const b = new ModelBuilder();
  const arch = civ.architecture;
  const style = civStyle(civ.key);
  const stone = mix(style.stone, arch.wall, 0.15);
  // Castles are always stone; towers and the wonder never go back to camp materials.
  let mat = matFor(civ.key, age);
  if (def.key === 'castle' || ((def.key === 'guardTower' || def.key === 'wonder') && age < 2)) mat = 'stone';
  const c: Ctx = { b, arch, age, mat, flavour: style.flavour, wall: arch.wall, frame: arch.wallDark, roofColor: arch.roof, trim: age === 3 ? GOLD : arch.trim, stone, smoke: [], top: 0.2 };
  dress(c, style.carved ?? DARK_WOOD);
  SURF = surfacesFor(c);
  const S = def.size;

  if (def.model.field) {
    farmField(b, def.model.w, def.model.d);
    b.box(0.3, 0.3, 0.3, T(0xd4b456), S / 2 - 0.35, 0.15, -S / 2 + 0.35);
    return { geometry: b.build(), smoke: [], height: 0.35, scale: 1 };
  }
  // Ground plinth for most buildings.
  const plinth = (w: number, d: number) => b.box(w, 0.1, d, T(darken(0x9a948a, 0.8)), 0, 0.05, 0);

  switch (def.key) {
    case 'house': {
      plinth(1.8, 1.6);
      const t = block(c, 0, 0, 1.55, 1.25, 0.8, { door: true });
      civRoof(c, 0, 0, 1.55, 1.25, t);
      chimney(c, 0.45, -0.3, t + 0.55);
      b.box(0.3, 0.08, 0.08, T(0x5a8a3a), -0.45, 0.42, 0.68);
      barrel(b, 0.7, 0.75);
      break;
    }
    case 'townCenter': {
      plinth(3.9, 3.9);
      const t1 = block(c, 0, 0.1, 3.5, 2.9, 1.0, { door: true });
      // Upper storey and roof.
      const t2 = block(c, 0, -0.1, 2.4, 2.0, 0.75, { baseY: t1 });
      civRoof(c, 0, -0.1, 2.4, 2.0, t2);
      b.box(3.6, 0.08, 3.0, T(darken(c.wall, 0.75)), 0, t1 + 0.02, 0.1);
      // Bell tower.
      const bx = 1.35;
      const bz = -1.25;
      const bt = block(c, bx, bz, 0.8, 0.8, 2.4, { windows: 0 });
      b.box(0.6, 0.45, 0.6, T(darken(c.wall, 0.9)), bx, bt + 0.22, bz);
      b.box(0.62, 0.3, 0.62, T(0x1e1a18), bx, bt + 0.22, bz);
      b.cyl(0.12, 0.17, 0.2, 10, T(0xc8a040), bx, bt + 0.2, bz);
      civRoof(c, bx, bz, 0.8, 0.8, bt + 0.45, c.arch.roofStyle === 'gable' || c.arch.roofStyle === 'hip' ? 'cone' : undefined);
      banner(b, -1.6, t1, 1.5, 0.8);
      chimney(c, -1.0, -0.6, t2 + 0.5);
      // Steps.
      b.box(1.0, 0.06, 0.3, T(darken(0x9a948a, 0.9)), 0, 0.03, 1.75);
      break;
    }
    case 'barracks': {
      plinth(2.9, 1.6);
      const t = block(c, 0, -0.65, 2.8, 1.4, 0.95, { door: true });
      civRoof(c, 0, -0.65, 2.8, 1.4, t);
      fence(b, 0, 0.75, 2.8, 1.3);
      dummy(b, -0.7, 0.75);
      dummy(b, 0.1, 0.9);
      weaponRack(b, 0.85, 0.35);
      banner(b, 1.3, 0.1, 1.3, 0.9);
      break;
    }
    case 'archeryRange': {
      plinth(1.4, 2.8);
      const t = block(c, -0.75, 0, 1.2, 2.6, 0.8, { door: true });
      civRoof(c, -0.75, 0, 1.2, 2.6, t);
      openShed(c, 0.55, -0.7, 1.3, 1.1, 0.7);
      target(b, 1.1, 0.4);
      target(b, 1.1, 1.1);
      target(b, 0.5, 1.15);
      b.box(0.4, 0.05, 0.4, T(0xc8b07a), 0.6, 0.03, 0.3);
      banner(b, -0.2, 0.1, 1.25, 0.9);
      break;
    }
    case 'stable': {
      plinth(2.9, 1.7);
      const t = block(c, 0, -0.6, 2.8, 1.5, 0.85, { windows: 0 });
      civRoof(c, 0, -0.6, 2.8, 1.5, t);
      // Open stall fronts with horses looking out.
      for (let i = 0; i < 4; i++) {
        const x = -1.05 + i * 0.7;
        b.box(0.5, 0.5, 0.05, T(0x1e1a18), x, 0.4, 0.17);
        b.box(0.52, 0.2, 0.06, T(WOOD), x, 0.2, 0.18);
      }
      horse(b, -0.7, 0.7, 0.4);
      horse(b, 0.6, 0.85, -0.6, 0x3a2a1a);
      hay(b, 1.15, 0.45);
      hay(b, -1.2, 0.9);
      b.box(0.5, 0.12, 0.15, T(WOOD), 0.05, 0.08, 1.15);
      b.box(0.44, 0.04, 0.1, T(0x4a7a9a), 0.05, 0.14, 1.15);
      fence(b, 0, 0.75, 2.8, 1.25);
      break;
    }
    case 'blacksmith': {
      plinth(2.6, 2.4);
      const t = block(c, -0.25, -0.3, 2.0, 1.7, 0.9, { door: true });
      civRoof(c, -0.25, -0.3, 2.0, 1.7, t);
      // Open forge lean-to with glowing hearth.
      openShed(c, 0.95, 0.55, 0.7, 1.0, 0.6);
      b.box(0.45, 0.3, 0.45, T(darken(c.stone, 0.75)), 0.95, 0.25, 0.4);
      b.box(0.3, 0.12, 0.3, T(FIRE), 0.95, 0.42, 0.4);
      chimney(c, 0.95, 0.2, t + 0.9, 0.22);
      // Anvil.
      b.box(0.2, 0.12, 0.1, T(IRON), 0.1, 0.25, 0.95);
      b.box(0.1, 0.18, 0.08, T(IRON), 0.1, 0.1, 0.95);
      weaponRack(b, -0.8, 0.85);
      barrel(b, 0.55, 1.05);
      break;
    }
    case 'market': {
      plinth(2.9, 2.9);
      const t = block(c, 0, -0.75, 2.6, 1.2, 0.85, { door: true });
      civRoof(c, 0, -0.75, 2.6, 1.2, t);
      for (let i = 0; i < 3; i++) {
        const x = -0.95 + i * 0.95;
        for (const sx of [-1, 1]) b.box(0.04, 0.55, 0.04, T(WOOD), x + sx * 0.32, 0.28, 0.75);
        b.box(0.75, 0.03, 0.55, T(i % 2 ? 0xffffff : 0xe8dcc0, i % 2 ? 1 : 0), x, 0.6, 0.75, 0.25, 0, 0);
        b.box(0.6, 0.18, 0.3, T(WOOD), x, 0.15, 0.85);
        const goods = [0xc8402a, 0xe8b030, 0x6a9a3a];
        for (let k = 0; k < 3; k++) b.sphere(0.06, T(goods[(i + k) % 3]), x - 0.15 + k * 0.15, 0.3, 0.85, 1, 0.8, 1, 0);
      }
      crate(b, 1.2, 1.2);
      crate(b, 1.0, 1.25, 0.16);
      barrel(b, -1.25, 1.2);
      banner(b, 1.35, 0.1, -0.2, 1.0);
      break;
    }
    case 'mill': {
      plinth(1.8, 1.8);
      // Tapered windmill tower.
      b.cyl(0.62, 0.8, 1.6, 12, T(c.wall), 0, 0.9, 0);
      for (const y of [0.6, 1.2]) windowOn(c, 'front', 0, 0, 1.3, 1.36, 0.3, y, 0.8);
      doorOn(c, 0, -0.03, 1.4, 0.1, 0.3, 0.45);
      b.cone(0.75, 0.7, 12, T(c.roofColor), 0, 2.05, 0);
      const hub: [number, number, number] = [0, 1.55, 0.85];
      b.cyl(0.08, 0.08, 0.2, 8, T(DARK_WOOD), hub[0], hub[1], 0.75, Math.PI / 2, 0, 0);
      for (let i = 0; i < 4; i++) {
        const a = (i * Math.PI) / 2 + 0.3;
        b.box(0.05, 0.95, 0.03, T(DARK_WOOD, 0, Part.Spin, hub), Math.sin(a) * 0.48, hub[1] + Math.cos(a) * 0.48, hub[2], 0, 0, -a);
        b.box(0.22, 0.7, 0.015, T(0xece4d0, 0, Part.Spin, hub), Math.sin(a) * 0.55 + Math.cos(a) * 0.1, hub[1] + Math.cos(a) * 0.55 - Math.sin(a) * 0.1, hub[2] + 0.02, 0, 0, -a);
      }
      for (let i = 0; i < 3; i++) b.sphere(0.1, T(0xd8c8a0), -0.6 + i * 0.16, 0.12, 0.7, 1, 0.8, 0.8, 0);
      c.top = 2.4;
      break;
    }
    case 'lumberCamp': {
      plinth(1.9, 1.7);
      openShed(c, -0.2, -0.2, 1.4, 1.1, 0.7);
      logPile(b, -0.2, -0.2, 4, 1.1);
      logPile(b, 0.6, 0.6, 3, 0.8);
      // Sawhorse and stump with axe.
      b.cyl(0.13, 0.15, 0.2, 8, T(0x8a6238), -0.6, 0.1, 0.65);
      b.box(0.03, 0.25, 0.03, T(DARK_WOOD), -0.6, 0.3, 0.65, 0.4, 0, 0);
      b.box(0.1, 0.06, 0.02, T(0xb8bcc4), -0.6, 0.4, 0.7, 0.4, 0, 0);
      break;
    }
    case 'miningCamp': {
      plinth(1.9, 1.7);
      const t = block(c, -0.35, -0.35, 1.1, 0.9, 0.6, { door: true, windows: 0.6 });
      civRoof(c, -0.35, -0.35, 1.1, 0.9, t);
      // Ore piles, cart and rails.
      b.sphere(0.25, T(0xd8b030), 0.55, 0.1, -0.35, 1.2, 0.6, 1, 0);
      b.sphere(0.25, T(0x9a948a), 0.6, 0.1, 0.4, 1.2, 0.6, 1, 0);
      for (const x of [-0.12, 0.12]) b.box(0.03, 0.02, 1.4, T(IRON), -0.2 + x, 0.02, 0.4);
      b.box(0.3, 0.18, 0.4, T(0x6a4a2a), -0.2, 0.18, 0.55);
      b.sphere(0.12, T(0xd8b030), -0.2, 0.3, 0.55, 1, 0.5, 1.2, 0);
      for (const z of [0.42, 0.68]) b.cyl(0.06, 0.06, 0.34, 8, T(IRON), -0.2, 0.07, z, 0, 0, Math.PI / 2);
      break;
    }
    case 'dock': {
      // Wooden pier on piles, boathouse and crane.
      b.box(2.8, 0.08, 2.8, T(0x8a6a42), 0, 0.12, 0);
      for (let i = 0; i < 6; i++) b.box(2.8, 0.01, 0.02, T(DARK_WOOD), 0, 0.165, -1.25 + i * 0.5);
      for (const x of [-1.3, 0, 1.3]) for (const z of [-1.3, 0, 1.3]) b.cyl(0.06, 0.06, 0.8, 6, T(DARK_WOOD), x, -0.25, z);
      const t = block(c, -0.8, -0.8, 1.0, 1.0, 0.7, { baseY: 0.16, door: true, windows: 0.6 });
      civRoof(c, -0.8, -0.8, 1.0, 1.0, t);
      b.box(0.08, 1.4, 0.08, T(WOOD), 0.9, 0.86, -0.9);
      b.box(0.9, 0.06, 0.06, T(WOOD), 0.55, 1.55, -0.9, 0, 0, 0.15);
      b.box(0.01, 0.5, 0.01, T(0x3a3a3a), 0.15, 1.25, -0.9);
      crate(b, 0.15, -0.9, 0.18, 0.16);
      for (let i = 0; i < 2; i++) b.cyl(0.12, 0.12, 0.06, 10, T(0xb89a6a), 0.9 - i * 0.3, 0.2, 0.9);
      banner(b, 1.2, 0.16, 1.2, 0.9);
      c.top = 1.6;
      break;
    }
    case 'watchtower': {
      plinth(1, 1);
      const t = block(c, 0, 0, 0.7, 0.7, 2.2, { windows: 0.5, color: c.age <= 1 ? mix(WOOD, c.wall, 0.3) : undefined });
      b.box(1.0, 0.08, 1.0, T(WOOD), 0, t, 0);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.box(0.05, 0.4, 0.05, T(WOOD), sx * 0.45, t + 0.2, sz * 0.45);
      civRoof(c, 0, 0, 0.95, 0.95, t + 0.4, c.arch.roofStyle === 'gable' || c.arch.roofStyle === 'hip' ? 'cone' : undefined);
      b.box(0.15, 1.8, 0.02, T(DARK_WOOD), 0.2, 0.95, 0.37, 0.12, 0, 0);
      break;
    }
    case 'guardTower': {
      plinth(1, 1);
      tower(c, 0, 0, 0.42, 2.8);
      doorOn(c, 0, 0, 0.88, 0.1, 0.24, 0.42);
      break;
    }
    case 'castle': {
      plinth(4, 4);
      // Curtain walls with walkway.
      const wh = 1.3;
      for (const [x, z, w, d] of [[0, 1.7, 3.2, 0.35], [0, -1.7, 3.2, 0.35], [1.7, 0, 0.35, 3.2], [-1.7, 0, 0.35, 3.2]] as const) {
        b.box(w, wh, d, T(stone), x, 0.1 + wh / 2, z);
        crenellationsAt(c, x, z, w, d, 0.1 + wh, stone);
      }
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) tower(c, sx * 1.7, sz * 1.7, 0.42, 2.0, stone);
      // Gatehouse.
      b.box(0.9, 1.6, 0.5, T(darken(stone, 0.95)), 0, 0.9, 1.75);
      b.box(0.45, 0.7, 0.06, T(0x1e1a18), 0, 0.45, 2.01);
      b.box(0.5, 0.06, 0.07, T(IRON), 0, 0.82, 2.02);
      // Keep, in the civilisation's own castle tradition.
      const kt = castleKeep(c, stone);
      banner(b, 0, kt, -0.2, 0.9);
      for (const x of [-1.0, 1.0]) b.box(0.03, 0.4, 0.3, T(0xffffff, 1), x, 0.8, 1.9);
      c.top = Math.max(c.top, kt + 0.9);
      break;
    }
    case 'siegeWorkshop': {
      plinth(2.9, 2.7);
      const t = block(c, -0.2, -0.4, 2.4, 1.8, 1.15, { windows: 0.6 });
      civRoof(c, -0.2, -0.4, 2.4, 1.8, t);
      // Big double doors.
      b.box(0.9, 0.85, 0.04, T(DARK_WOOD), -0.2, 0.52, 0.52);
      b.box(0.04, 0.85, 0.05, T(IRON), -0.2, 0.52, 0.53);
      // A-frame crane and parts.
      b.box(0.06, 1.5, 0.06, T(WOOD), 1.15, 0.8, 0.6, 0, 0, 0.25);
      b.box(0.06, 1.5, 0.06, T(WOOD), 1.15, 0.8, 0.6, 0, 0, -0.25);
      b.box(0.04, 0.04, 0.9, T(WOOD), 1.15, 1.5, 0.45);
      for (const x of [-0.8, -0.4]) b.cyl(0.18, 0.18, 0.06, 12, T(DARK_WOOD), x, 0.18, 1.0, 0, 0, Math.PI / 2);
      logPile(b, 0.4, 1.05, 2, 0.9);
      chimney(c, -1.1, -0.9, t + 0.5);
      break;
    }
    case 'university': {
      plinth(2.9, 2.9);
      const t = block(c, 0, -0.25, 2.6, 2.0, 1.25, { door: true });
      roof(b, c.arch.roofStyle === 'pagoda' ? 'pagoda' : 'flat', 2.6, 2.0, t, c.roofColor, c.trim, 0, -0.25);
      const r = 0.75;
      b.cyl(r * 1.05, r * 1.05, 0.25, 16, T(c.wall), 0, t + 0.12, -0.25);
      b.add(new THREE.SphereGeometry(r, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), T(c.arch.roofStyle === 'dome' ? c.roofColor : 0x6a8a8a), 0, t + 0.25, -0.25);
      b.cyl(0.05, 0.08, 0.35, 6, T(c.trim), 0, t + 0.25 + r + 0.15, -0.25);
      // Columned portico with pediment.
      b.box(2.0, 0.08, 0.6, T(darken(0x9a948a, 0.9)), 0, 0.12, 1.05);
      for (let i = 0; i < 6; i++) b.cyl(0.06, 0.07, 1.1, 8, T(0xece6d8), -0.85 + i * 0.34, 0.7, 1.15);
      b.box(2.1, 0.12, 0.7, T(0xece6d8), 0, 1.3, 1.05);
      b.prism(2.1, 0.35, 0.7, T(0xece6d8), 0, 1.36, 1.05);
      c.top = Math.max(c.top, t + 0.25 + r + 0.3);
      break;
    }
    case 'monastery': {
      plinth(2.9, 2.8);
      // Nave running front-to-back, bell tower with spire at the front.
      const t = block(c, 0.2, -0.3, 1.6, 2.2, 1.2, { windows: 0.8 });
      civRoof(c, 0.2, -0.3, 1.6, 2.2, t, c.arch.roofStyle === 'hip' ? 'gable' : undefined);
      const bt = block(c, -0.9, 0.7, 0.8, 0.8, 2.3, { door: true, windows: 0.4 });
      b.box(0.82, 0.35, 0.82, T(0x1e1a18), -0.9, bt - 0.25, 0.7);
      if (c.arch.roofStyle === 'gable' || c.arch.roofStyle === 'hip') {
        b.cone(0.55, 1.3, 4, T(c.roofColor), -0.9, bt + 0.65, 0.7, 0, Math.PI / 4, 0);
        b.box(0.03, 0.35, 0.03, T(0xc8a040), -0.9, bt + 1.45, 0.7);
        b.box(0.2, 0.03, 0.03, T(0xc8a040), -0.9, bt + 1.5, 0.7);
        c.top = Math.max(c.top, bt + 1.6);
      } else civRoof(c, -0.9, 0.7, 0.8, 0.8, bt);
      // Rose window on the nave front.
      b.cyl(0.24, 0.24, 0.03, 16, T(c.trim), 0.2, t - 0.35, 0.81, Math.PI / 2, 0, 0);
      b.cyl(0.19, 0.19, 0.035, 16, T(0x5a7aa8), 0.2, t - 0.35, 0.815, Math.PI / 2, 0, 0);
      break;
    }
    case 'arsenal': {
      plinth(3, 2.7);
      const t = block(c, 0, -0.45, 2.8, 1.6, 1.1, { door: true });
      civRoof(c, 0, -0.45, 2.8, 1.6, t);
      chimney(c, 1.0, -0.9, t + 0.6);
      for (const x of [-0.8, 0.6]) {
        b.cyl(0.07, 0.09, 0.7, 10, T(0x2a2a2e), x, 0.25, 0.85, Math.PI / 2 - 0.2, 0, 0);
        for (const sx of [-1, 1]) b.cyl(0.14, 0.14, 0.04, 12, T(DARK_WOOD), x + sx * 0.14, 0.14, 0.75, 0, 0, Math.PI / 2);
      }
      for (let i = 0; i < 6; i++) b.sphere(0.06, T(0x18181c), -0.1 + (i % 3) * 0.12, 0.06 + Math.floor(i / 3) * 0.1, 1.15 + (i >= 3 ? 0 : 0.0));
      banner(b, -1.3, 0.1, 1.2, 1.0);
      break;
    }
    case 'foundry': {
      plinth(3, 2.8);
      const t = block(c, 0, -0.3, 2.8, 2.0, 1.2, { windows: 0.7, color: c.age >= 2 && !isModern(c.mat) ? mix(0x8a4a36, c.wall, 0.2) : undefined });
      civRoof(c, 0, -0.3, 2.8, 2.0, t, 'flat');
      chimney(c, -0.9, -0.9, t + 1.6, 0.3);
      chimney(c, 0.3, -0.9, t + 1.3, 0.3);
      // Glowing furnace mouth.
      b.box(0.8, 0.55, 0.05, T(0x1e1a18), 0.6, 0.45, 0.72);
      b.box(0.6, 0.3, 0.06, T(FIRE), 0.6, 0.35, 0.73);
      for (let i = 0; i < 3; i++) b.box(0.25, 0.08, 0.12, T(0x6a6a70), -0.8 + i * 0.3, 0.06, 1.0);
      break;
    }
    case 'industrialWorkshop': {
      plinth(3, 2.6);
      const t = block(c, 0, -0.2, 2.8, 2.0, 1.0, { door: true, windows: 0.9, color: mix(0x8a4a36, c.wall, 0.15) });
      // Saw-tooth factory roof with skylights.
      for (let i = 0; i < 4; i++) {
        const x = -1.05 + i * 0.7;
        b.prism(0.7, 0.4, 2.0, T(0x4a4e56), x, t, -0.2, Math.PI / 2);
        b.box(0.02, 0.35, 1.9, T(GLASS), x + 0.33, t + 0.18, -0.2, 0, 0, -0.5);
      }
      chimney(c, 1.15, -1.0, t + 1.8, 0.28);
      c.top = Math.max(c.top, t + 0.4);
      crate(b, -1.1, 1.05);
      crate(b, -0.85, 1.1, 0.16);
      break;
    }
    case 'logisticsDepot': {
      plinth(3, 2.6);
      const t = block(c, 0, -0.6, 2.8, 1.3, 1.0, { windows: 0.6 });
      civRoof(c, 0, -0.6, 2.8, 1.3, t);
      for (let i = 0; i < 3; i++) b.box(0.6, 0.65, 0.04, T(DARK_WOOD), -0.9 + i * 0.9, 0.42, 0.07);
      for (const z of [0.55, 0.75]) b.box(2.9, 0.03, 0.05, T(IRON), 0, 0.03, z);
      for (let i = 0; i < 7; i++) b.box(0.08, 0.02, 0.4, T(DARK_WOOD), -1.3 + i * 0.43, 0.015, 0.65);
      b.box(0.8, 0.3, 0.4, T(0x6a4a2a), 0.3, 0.25, 0.65);
      for (const x of [0.0, 0.6]) b.cyl(0.08, 0.08, 0.42, 8, T(IRON), x, 0.08, 0.65, 0, 0, Math.PI / 2);
      for (let i = 0; i < 4; i++) crate(b, -1.1 + (i % 2) * 0.24, 1.05, 0.2, Math.floor(i / 2) * 0.2);
      break;
    }
    case 'wonder': {
      plinth(5, 5);
      let y = 0.1;
      let w = 4.6;
      for (let i = 0; i < 3; i++) {
        b.box(w, 0.45, w, T(i % 2 ? darken(c.wall, 0.92) : c.wall), 0, y + 0.22, 0);
        y += 0.45;
        w *= 0.78;
      }
      const t = block(c, 0, 0, w, w, 1.3, { door: true, baseY: y });
      civRoof(c, 0, 0, w, w, t, c.arch.roofStyle === 'gable' ? 'hip' : undefined);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) tower(c, sx * 2.0, sz * 2.0, 0.3, 2.6);
      b.box(1.2, 0.1, 1.3, T(darken(0x9a948a, 0.9)), 0, 0.15, 2.4);
      banner(b, 0, c.top, 0, 1.0);
      c.top += 1;
      break;
    }
    default: {
      // Generic fallback (should not normally be used).
      plinth(S * 0.9, S * 0.9);
      const t = block(c, 0, 0, S * 0.8, S * 0.8, def.model.wallH, { door: true });
      civRoof(c, 0, 0, S * 0.8, S * 0.8, t);
    }
  }
  // Docks sit on their piles and towers on a single tile at full size; everything else grows with
  // the ages inside a yard that marks the whole plot.
  const k = full || def.key === 'dock' || S < 2 ? 1 : AGE_SCALE[Math.max(0, Math.min(4, age))];
  if (k >= 1) return { geometry: b.build(), smoke: c.smoke, height: c.top, scale: 1 };
  const out = new ModelBuilder();
  yard(out, c, S);
  out.merge(b, 0, 0, 0, 0, k);
  return { geometry: out.build(), smoke: c.smoke.map((v) => v.clone().multiplyScalar(k)), height: Math.max(0.3, c.top * k), scale: k };
}

/** Ground and boundary of a building's plot, in the materials of the age. */
function yard(b: ModelBuilder, c: Ctx, S: number): void {
  const half = S / 2 - 0.06;
  const age = c.age;
  // Packed earth, then strewn straw, flagstones and finally dressed paving. Deep enough that uneven
  // ground never shows through.
  const ground = [0x7a6446, 0x8a7650, 0x8a867c, 0xb4ae9e][Math.min(3, age)];
  if (age >= 2) SURF.set(ground, Surf.Stone);
  b.box(half * 2, 0.24, half * 2, T(ground), 0, -0.1, 0);
  if (age >= 2) {
    // Paving joints.
    const n = Math.round(S * 2);
    for (let i = 1; i < n; i++) {
      const t = -half + (i * half * 2) / n;
      b.box(0.02, 0.005, half * 2, T(darken(ground, 0.8)), t, 0.022, 0);
      b.box(half * 2, 0.005, 0.02, T(darken(ground, 0.8)), 0, 0.022, t);
    }
  } else {
    // A few worn patches and scattered straw.
    for (let i = 0; i < S * 3; i++) {
      const hx = Math.sin(i * 12.9898 + S) * 43758.5453;
      const hz = Math.sin(i * 39.3468 + S) * 24634.6345;
      const x = (hx - Math.floor(hx) - 0.5) * half * 1.7;
      const z = (hz - Math.floor(hz) - 0.5) * half * 1.7;
      b.box(0.18 + (i % 3) * 0.08, 0.006, 0.12 + (i % 2) * 0.1, T(i % 2 ? darken(ground, 0.85) : 0xb8a060), x, 0.022, z, 0, i * 0.7, 0);
    }
  }
  // Boundary with an opening at the front, where the doors face.
  const gate = Math.min(0.8, S * 0.25);
  const side = (x0: number, z0: number, x1: number, z1: number) => {
    const len = Math.hypot(x1 - x0, z1 - z0);
    const ax = Math.abs(x1 - x0) > 0.01;
    const mx = (x0 + x1) / 2;
    const mz = (z0 + z1) / 2;
    if (age === 0 || age === 1) {
      // Rough stakes with one rail, then a woven wattle fence.
      const n = Math.max(2, Math.round(len / (age === 0 ? 0.4 : 0.3)));
      for (let i = 0; i <= n; i++) b.cyl(0.025, 0.03, 0.3, 5, T(DARK_WOOD), x0 + ((x1 - x0) * i) / n, 0.15, z0 + ((z1 - z0) * i) / n);
      b.box(ax ? len : 0.03, 0.03, ax ? 0.03 : len, T(0x6a5038), mx, 0.22, mz);
      if (age === 1) {
        b.box(ax ? len : 0.05, 0.12, ax ? 0.05 : len, T(0x9a7a4a), mx, 0.12, mz);
        b.box(ax ? len : 0.055, 0.025, ax ? 0.055 : len, T(0x7a5a34), mx, 0.12, mz);
      }
    } else if (age === 2) {
      // Low dry-stone wall.
      b.box(ax ? len + 0.12 : 0.12, 0.18, ax ? 0.12 : len + 0.12, T(c.stone), mx, 0.09, mz);
      b.box(ax ? len + 0.14 : 0.14, 0.04, ax ? 0.14 : len + 0.14, T(darken(c.stone, 0.85)), mx, 0.2, mz);
    } else {
      // Stone kerb with iron railings and gilded finials.
      b.box(ax ? len + 0.1 : 0.1, 0.08, ax ? 0.1 : len + 0.1, T(0xd8d2c4), mx, 0.04, mz);
      const n = Math.max(2, Math.round(len / 0.2));
      for (let i = 0; i <= n; i++) {
        const x = x0 + ((x1 - x0) * i) / n;
        const z = z0 + ((z1 - z0) * i) / n;
        b.box(0.02, 0.24, 0.02, T(0x2a2a2e), x, 0.2, z);
        if (i % 2 === 0) b.sphere(0.022, T(GOLD), x, 0.33, z);
      }
      b.box(ax ? len : 0.025, 0.025, ax ? 0.025 : len, T(0x2a2a2e), mx, 0.3, mz);
    }
  };
  side(-half, -half, half, -half);
  side(-half, -half, -half, half);
  side(half, -half, half, half);
  side(-half, half, -gate / 2, half);
  side(gate / 2, half, half, half);
}

/** The castle's central keep. Returns its top. */
function castleKeep(c: Ctx, stone: number): number {
  const b = c.b;
  const z = -0.2;
  switch (c.flavour) {
    case 'japanese': {
      // Tenshu: sloped stone base with stacked white storeys under dark tiled roofs.
      const g = new THREE.CylinderGeometry(Math.SQRT1_2 * 0.82, Math.SQRT1_2, 1, 4, 1);
      g.rotateY(Math.PI / 4);
      b.add(g, T(stone), 0, 0.1 + 0.4, z, 0, 0, 0, 2.1, 0.8, 2.1);
      let y = 0.9;
      let s = 1.5;
      for (let i = 0; i < 3; i++) {
        const h = 0.5 - i * 0.05;
        b.box(s, h, s, T(0xf2eee6), 0, y + h / 2, z);
        uprights(c, 0, z, s, s, y, h, 0.4, 0.05, DARK_WOOD);
        y = roof(b, 'hip', s + 0.3, s + 0.3, y + h, c.arch.roof, DARK_WOOD, 0, z) - (i < 2 ? 0.12 : 0);
        s *= 0.72;
      }
      b.cyl(0.02, 0.02, 0.22, 5, T(GOLD), 0, y + 0.1, z);
      return y + 0.2;
    }
    case 'chinese': {
      // Grey brick platform and a red hall under a double-eaved roof.
      b.box(2.1, 0.75, 2.1, T(stone), 0, 0.1 + 0.37, z);
      crenellationsAt(c, 0, z, 2.1, 2.1, 0.85, stone);
      b.box(1.5, 0.9, 1.3, T(0xa8382a), 0, 0.85 + 0.45, z);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) b.cyl(0.06, 0.06, 0.9, 8, T(0x8a2418), sx * 0.8, 0.85 + 0.45, z + sz * 0.7);
      ring(c, 0, z, 1.5, 1.3, 1.7, 0.08, GOLD, 0.03, 0.03);
      const t = roof(b, 'pagoda', 1.7, 1.5, 1.75, c.arch.roof, GOLD, 0, z);
      return t;
    }
    case 'aztec': {
      let y = 0.1;
      let s = 2.0;
      for (let i = 0; i < 4; i++) {
        b.box(s, 0.45, s, T(i % 2 ? darken(stone, 0.9) : stone), 0, y + 0.22, z);
        y += 0.45;
        s *= 0.8;
      }
      b.box(0.4, 1.8, 0.1, T(darken(stone, 0.85)), 0, 0.9, z + 1.0, 0.45, 0, 0);
      b.box(s * 0.8, 0.5, s * 0.8, T(c.arch.trim), 0, y + 0.25, z);
      return y + 0.5;
    }
    case 'ottoman':
    case 'indian':
    case 'steppe': {
      const kt = block(c, 0, z, 1.7, 1.7, 2.0, { windows: 0.6, color: stone });
      crenellationsAt(c, 0, z, 1.7, 1.7, kt, stone);
      const r = 0.6;
      b.cyl(r, r, 0.2, 14, T(stone), 0, kt + 0.1, z);
      b.add(new THREE.SphereGeometry(r, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), T(c.roofColor), 0, kt + 0.2, z);
      b.cyl(0.02, 0.03, 0.3, 5, T(GOLD), 0, kt + 0.2 + r + 0.12, z);
      return kt + r + 0.3;
    }
    case 'french': {
      // Chateau keep with a steep slate roof.
      const kt = block(c, 0, z, 1.7, 1.7, 2.1, { windows: 0.6, color: stone });
      return roof(b, 'hip', 1.7, 1.7, kt, c.arch.roof, c.trim, 0, z);
    }
    default: {
      const kt = block(c, 0, z, 1.7, 1.7, 2.3, { windows: 0.6, color: stone });
      crenellationsAt(c, 0, z, 1.7, 1.7, kt, stone);
      // Corner turrets rising above the keep's battlements.
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
        b.cyl(0.17, 0.17, kt + 0.2, 10, T(stone), sx * 0.85, (kt + 0.2) / 2 + 0.05, z + sz * 0.85);
        crenellationsAt(c, sx * 0.85, z + sz * 0.85, 0.34, 0.34, kt + 0.25, stone);
      }
      return kt;
    }
  }
}

/** Thatch for bamboo buildings: dark rice straw in Japan, pale straw in China, palm leaf in India. */
function leafRoof(f: Flavour): number {
  return f === 'japanese' ? 0x7a6a48 : f === 'indian' ? 0x8a8a48 : 0xb8a058;
}

/** Which surface texture each of the building's colours gets. */
function surfacesFor(c: Ctx): Map<number, Surf> {
  const m = new Map<number, Surf>();
  const wallSurf: Record<Mat, Surf> = {
    wicker: Surf.None, log: Surf.Wood, canvas: Surf.Canvas, bamboo: Surf.None, daub: Surf.None, plank: Surf.Wood, woodGrass: Surf.None,
    bambooGrass: Surf.None, stone: Surf.Stone, carved: Surf.None, marble: Surf.None, steel: Surf.None, concrete: Surf.None, brick: Surf.Brick,
  };
  const roofSurf = isThatched(c.mat) ? Surf.Thatch : c.mat === 'canvas' ? Surf.Canvas : isModern(c.mat) ? Surf.None : Surf.Tile;
  for (const w of [WOOD, DARK_WOOD, 0x6a5038, 0x5a4028, 0x8a6238, 0x7a5430, 0x6a4a2c, 0x9a7444, 0x7a5a32, 0x9a7a4a, 0x7a5a34, 0x6a4a2a]) m.set(w, Surf.Wood);
  for (const k of [1, 0.85, 0.9, 0.95, 1.08]) m.set(darken(c.stone, k), Surf.Stone);
  m.set(darken(0x9a948a, 0.8), Surf.Stone);
  m.set(darken(0x9a948a, 0.9), Surf.Stone);
  if (c.mat === 'log' || c.mat === 'plank' || c.mat === 'daub' || c.mat === 'woodGrass') m.set(c.frame, Surf.Wood);
  m.set(c.roofColor, roofSurf);
  m.set(darken(c.roofColor, 0.78), roofSurf);
  m.set(SHINGLE, Surf.Tile);
  m.set(THATCH, Surf.Thatch);
  // Japanese and Chinese castle-age walls are plaster or lacquer above a stone base.
  const plastered = c.mat === 'stone' && (c.flavour === 'japanese' || c.flavour === 'chinese');
  m.set(c.wall, plastered ? Surf.None : wallSurf[c.mat]);
  // Tent and yurt roofs are drawn a shade darker than the canvas walls.
  if (c.mat === 'canvas') for (const k of [0.94, 0.96]) m.set(darken(c.wall, k), Surf.Canvas);
  return m;
}

/** Wall, frame and roof colours for the block's material. */
function dress(c: Ctx, carved: number): void {
  const a = c.arch;
  const f = c.flavour;
  switch (c.mat) {
    case 'wicker':
      c.wall = f === 'aztec' ? 0xb89a68 : 0xa88a5a;
      c.roofColor = f === 'aztec' ? 0xd2b878 : THATCH;
      break;
    case 'log':
      c.wall = 0x6a4a2c;
      c.roofColor = SHINGLE;
      break;
    case 'canvas':
      c.wall = mix(0xece4d0, a.wall, 0.3);
      c.roofColor = c.wall;
      c.trim = f === 'steppe' ? 0x2a5a9a : f === 'ottoman' ? 0xa82a2a : 0x3a6a8a;
      break;
    case 'bamboo':
      c.wall = f === 'indian' ? 0xc8a868 : 0xc8b878;
      c.roofColor = leafRoof(f);
      break;
    case 'daub':
      c.wall = a.wall;
      break;
    case 'plank':
      c.wall = 0x8a6238;
      c.roofColor = SHINGLE;
      break;
    case 'woodGrass':
      c.wall = mix(0xc8a878, a.wall, 0.4);
      c.roofColor = f === 'egyptian' || f === 'aztec' ? 0xd2b878 : THATCH;
      break;
    case 'bambooGrass':
      c.wall = mix(0xe8dcc0, a.wall, 0.5);
      c.frame = 0xb0a058;
      c.roofColor = leafRoof(f);
      break;
    case 'stone':
      c.wall = f === 'japanese' ? 0xf0ece4 : f === 'chinese' ? 0xa8382a : c.stone;
      break;
    case 'carved':
      c.wall = f === 'chinese' || f === 'steppe' ? carved : f === 'japanese' ? 0xece6da : 0xece4d4;
      c.frame = f === 'chinese' || f === 'steppe' ? GOLD : carved;
      break;
    case 'marble':
      c.wall = f === 'aztec' ? mix(c.stone, 0xeeeae2, 0.5) : f === 'indian' ? 0xf4f0e8 : 0xeeeae2;
      if (a.roofStyle === 'dome') c.roofColor = f === 'indian' ? 0xf4f0e8 : f === 'steppe' ? GOLD : 0x8a96a0;
      break;
    case 'steel':
      c.wall = 0x6a7480;
      c.roofColor = 0x56606a;
      c.frame = 0x3a424a;
      break;
    case 'concrete':
      c.wall = 0xb8b6b0;
      c.roofColor = 0x9a9890;
      break;
    case 'brick':
      c.wall = f === 'indian' ? 0xa85a3c : f === 'iberian' ? 0xb0624a : 0x9a4a36;
      c.roofColor = mix(a.roof, 0x4a4e56, 0.45);
      break;
  }
}

/** Scaffolding frame for buildings under construction. */
export function buildScaffold(size: number, h: number): THREE.BufferGeometry {
  const b = new ModelBuilder();
  const s = size * 0.5 + 0.08;
  const posts = Math.max(2, size + 1);
  const wood = 0x8a6a3a;
  for (let i = 0; i < posts; i++) {
    const t = -s + (i * 2 * s) / (posts - 1);
    for (const [x, z] of [[t, s], [t, -s], [s, t], [-s, t]]) b.box(0.04, h, 0.04, T(wood), x, h / 2, z);
  }
  for (let y = 0.35; y < h; y += 0.4) {
    b.box(2 * s, 0.03, 0.04, T(wood), 0, y, s);
    b.box(2 * s, 0.03, 0.04, T(wood), 0, y, -s);
    b.box(0.04, 0.03, 2 * s, T(wood), s, y, 0);
    b.box(0.04, 0.03, 2 * s, T(wood), -s, y, 0);
  }
  return b.build();
}

/** Rubble pile left by destroyed buildings. */
export function buildRubble(): THREE.BufferGeometry {
  const b = new ModelBuilder();
  let seed = 7;
  const r = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 22; i++) {
    const x = (r() - 0.5) * 0.9;
    const z = (r() - 0.5) * 0.9;
    b.box(0.08 + r() * 0.12, 0.05 + r() * 0.08, 0.08 + r() * 0.12, T(r() > 0.4 ? 0x7a746a : 0x4a3a2a), x, 0.03, z, r(), r() * 3, r());
  }
  return b.build();
}
